import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * InlineToast primitive — a CSS-positioned toast banner extracted from the
 * ad-hoc `previewToast` and `AutoSeededToast` patterns that appear across
 * Workspace.tsx and related components.
 *
 * NOT a Radix-managed toast: the existing ad-hoc usage drives visibility via
 * plain React state (`useState<Toast | null>`) and auto-fade via `useEffect`,
 * not via the Radix toast queue. This component preserves that ergonomic while
 * giving a single reusable surface with consistent tokens (§2 of
 * `docs/design/design-system.md`).
 *
 * Tones:
 *   - `info`    (default) — sky border/surface
 *   - `success` — emerald border/surface
 *   - `error`   — red border/surface; sticky (no auto-fade) + dismiss button
 *
 * Positioning: `fixed right-4 top-16 z-[55]` by default (matches the
 * preview-recovery toast z-layer from Workspace.tsx — sits above the preview
 * overlay (z-50) and below user-blocking modals (z-[60])).
 *
 * Usage:
 *
 *   {toast && (
 *     <InlineToast
 *       tone={toast.tone}
 *       onDismiss={() => setToast(null)}
 *     >
 *       {toast.message}
 *     </InlineToast>
 *   )}
 */

export type ToastTone = 'info' | 'success' | 'error';

export interface InlineToastProps {
  tone?: ToastTone;
  /** Called when the user clicks the dismiss button (error toasts only). */
  onDismiss?: () => void;
  /** Override positioning/sizing classes (default: `fixed right-4 top-16 z-[55]`). */
  className?: string;
  children: React.ReactNode;
  /** Forwarded to the root element for e2e selectors. */
  'data-testid'?: string;
  /** Forwarded to the root element for tone-based test selectors. */
  'data-tone'?: string;
}

export function InlineToast({
  tone = 'info',
  onDismiss,
  className,
  children,
  'data-testid': testId,
  'data-tone': dataTone,
}: InlineToastProps): React.JSX.Element {
  const toneClasses: Record<ToastTone, string> = {
    error: 'flex items-start gap-2 border border-red-500/30 bg-red-900/80 text-red-200',
    success: 'border border-emerald-500/30 bg-emerald-900/80 text-emerald-100',
    info: 'border border-sky-500/30 bg-sky-900/80 text-sky-100',
  };

  return (
    <div
      data-testid={testId}
      data-tone={dataTone ?? tone}
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'fixed right-4 top-16 z-[55] max-w-sm rounded-lg px-4 py-3 text-sm shadow-lg backdrop-blur-sm',
        toneClasses[tone],
        className,
      )}
    >
      <span className="flex-1">{children}</span>
      {tone === 'error' && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          data-testid={testId ? `${testId}-dismiss` : undefined}
          aria-label="Dismiss"
          className="app-region-no-drag -mt-0.5 -mr-1 shrink-0 rounded p-0.5 text-red-300 transition-colors hover:bg-red-500/20 hover:text-red-100"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
